import { addDays, canteenListFileName, dmy, todayIso } from '@kancelaria/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { api, errorText } from '../../api/client.js';
import { toast } from '../../store/ui.js';
import { Field, Modal } from '../ui.js';
import { DateInput } from '../DatePicker.js';
import { copyDayImage } from './canteen-image.js';

interface CanteenData {
  meals: Array<{
    meal: number;
    title: string;
    total: number;
    reports: number;
    blocks: Array<{ unit: string; people: string[] }>;
  }>;
}

/** Список ПЗ: хто фактично приходить у їдальню. */
export function CanteenModal({ onClose }: { onClose: () => void }): ReactNode {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState<CanteenData | null>(null);
  const [busy, setBusy] = useState<'' | 'full' | 'short'>('');
  const [snap, setSnap] = useState(false);

  /** Весь день однією картинкою в буфер — черговий кидає її в чат. */
  const copyImage = async (): Promise<void> => {
    if (!data?.meals.length) return;
    setSnap(true);
    try {
      const how = await copyDayImage(dmy(date), data.meals);
      toast(how === 'copied' ? 'Картинку скопійовано' : 'Буфер недоступний — картинку збережено файлом', 'ok');
    } catch (e) {
      toast(errorText(e), 'err');
    } finally {
      setSnap(false);
    }
  };

  /** Розхід за цю ж дату: черговому його зручно просто скопіювати. */
  const copy = async (kind: 'full' | 'short'): Promise<void> => {
    setBusy(kind);
    try {
      const res =
        kind === 'full'
          ? await api.post<{ text: string }>('/api/documents/expense', {
              from: date,
              to: date,
              showZero: true,
            })
          : await api.post<{ text: string }>('/api/documents/expense-tally', { date });
      await navigator.clipboard.writeText(res.text);
      toast('Розхід скопійовано', 'ok');
    } catch (e) {
      toast(errorText(e), 'err');
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    let alive = true;
    api
      .post<CanteenData>('/api/documents/canteen', { date })
      .then(d => alive && setData(d))
      .catch(e => toast(errorText(e), 'err'));
    return () => {
      alive = false;
    };
  }, [date]);

  return (
    <Modal
      title="Список ПЗ"
      onClose={onClose}
      width={860}
      footer={
        <>
          <button
            className="btn"
            disabled={!!busy}
            title="Розхід по групах: скільки їсть кожна, всього та компенсація"
            onClick={() => void copy('full')}
          >
            {busy === 'full' ? 'Готую…' : 'Розхід ВОКСД'}
          </button>
          <button
            className="btn"
            disabled={!!busy}
            title="Рядок на прийом їжі: порції, компенсація та скільки з них зростом 190+"
            onClick={() => void copy('short')}
          >
            {busy === 'short' ? 'Готую…' : 'Розхід ВМЗ'}
          </button>
          <button
            className="btn"
            disabled={snap || !data?.meals.length}
            title="Весь список за день однією картинкою — вставити в чат"
            onClick={() => void copyImage()}
          >
            {snap ? 'Готую…' : 'Картинкою'}
          </button>
          <button
            className="btn primary"
            onClick={() => void api.download(`/api/documents/canteen.xlsx?date=${date}`, canteenListFileName(date))}
          >
            Завантажити .xlsx
          </button>
        </>
      }
    >
      <div className="field-row">
        <Field label="Дата">
          <DateInput value={date} onChange={d => setDate(d)} />
        </Field>
        <div className="btn-row" style={{ marginBottom: 12 }}>
          {/* Швидкий вибір дня: кнопки тієї ж висоти, що й поле дати. */}
          {[
            { label: 'Сьогодні', days: 0 },
            { label: 'Завтра', days: 1 },
            { label: 'Післязавтра', days: 2 },
          ].map(p => {
            const value = addDays(todayIso(), p.days);
            return (
              <button
                key={p.label}
                className={`btn${date === value ? ' primary' : ''}`}
                onClick={() => setDate(value)}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {!data?.meals.length ? (
        <p className="muted">За цю дату ніхто не подав рапорт — список ПЗ не потрібен.</p>
      ) : (
        data.meals.map(m => (
          <div className="card" key={m.meal} style={{ marginBottom: 12 }}>
            <h2>{m.title}</h2>
            <p className="sub">
              Харчується: <b>{m.total}</b>, рапортів: {m.reports}
            </p>
            <div className="auto-grid" style={{ ['--min' as string]: '200px' }}>
              {m.blocks.map(b => (
                <div key={b.unit}>
                  <div className="pill on" style={{ marginBottom: 6 }}>
                    {b.unit} — {b.people.length}
                  </div>
                  <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                    {b.people.map(p => (
                      <li key={p}>{p}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </Modal>
  );
}
